<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { acceptOfferAction } from '@/api/functions/offers/accept-offer-action'
import { deleteOfferAction } from '@/api/functions/offers/delete-offer-action'
import { getOfferAction } from '@/api/functions/offers/get-offer-by-id-action'
import { rejectOfferAction } from '@/api/functions/offers/reject-offer-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const toast = useToast()
const route = useRoute()
const offerId = computed(() => route.params.id as string)

const { data: offerData, refetch } = useQueryApi({
  queryKey: ['offer', offerId],
  queryFn: () => getOfferAction(offerId.value),
  enabled: !!offerId.value,
  staleTime: 1000 * 60 * 3,
})

const offer = computed(() => offerData.value?.data || null)

const showDeleteModal = ref(false)
const showRejectModal = ref(false)
const showAcceptModal = ref(false)

const cardType = computed(() => {
  return offer?.value?.type === 'BUY' ? true : false
})

// DELETE OFFER
const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deleteOfferAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'پیشنهاد با موفقیت حذف شد')
    refetch()
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteOffer = async () => {
  if (!offerId.value) return
  deleteMutation.mutate(offerId.value)
}

// REJECT OFFER
const rejectMutation = useMutationApi({
  mutationFn: (id: string) => rejectOfferAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'پیشنهاد با موفقیت رد شد')
    refetch()
    showRejectModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleRejectOffer = async () => {
  if (!offerId.value) return
  rejectMutation.mutate(offerId.value)
}

// ACCEPT OFFER
const acceptMutation = useMutationApi({
  mutationFn: (id: string) => acceptOfferAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'پیشنهاد با موفقیت تایید شد')
    refetch()
    showAcceptModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleAcceptOffer = async () => {
  if (!offerId.value) return
  acceptMutation.mutate(offerId.value)
}
</script>

<template>
    <div class="mb-9">
        <ConfirmModal v-model="showDeleteModal" title="حذف پیشنهاد" :message="'آیا از حذف این پیشنهاد اطمینان دارید؟'"
            confirm-text="بله، حذف شود" cancel-text="بازگشت" @confirm="handleDeleteOffer"
            @cancel="showDeleteModal = false" />

        <ConfirmModal v-model="showRejectModal" title="رد پیشنهاد" :message="'آیا از رد این پیشنهاد اطمینان دارید؟'"
            confirm-text="بله، رد شود" cancel-text="بازگشت" @confirm="handleRejectOffer"
            @cancel="showRejectModal = false" />

        <ConfirmModal v-model="showAcceptModal" title="تایید پیشنهاد"
            :message="'آیا از تایید این پیشنهاد اطمینان دارید؟'" confirm-text="بله، تایید شود" cancel-text="بازگشت"
            @confirm="handleAcceptOffer" @cancel="showAcceptModal = false" />


        <router-link to="/offers"
            class="w-fit mb-4 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
            <img src="/svg/back-icon.svg" class="me-1" width="22" />
            <span>بازگشت</span>
        </router-link>

        <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-6">

            <div class="w-full lg:w-auto flex flex-col lg:flex-row lg:items-center gap-2 ">
                <div class="text-[#1b325a] text-[22px]">
                    <span>پیشنهاد شماره<span class="font-fa ms-1">{{ offer?.code }}</span></span>
                </div>
                <div class="w-full lg:w-auto flex flex-row items-center justify-between lg:justify-start gap-2">

                    <!-- status  -->
                    <div class="w-fit flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-6 m ms-6 me-2"
                        :class="offer?.status === 'EXPIRED' ? 'bg-[#fff8e7] text-[#ffba0e] ' :
                            offer?.status === 'PENDING' ? 'bg-[#d5ddea] text-[#2d5396] ' :
                                offer?.status === 'ACCEPTED' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                                    offer?.status === 'REJECTED' ? 'bg-[#fde7ef] text-[#ed145b]' :
                                        ' '">
                        <span>
                            {{
                                offer?.status === 'EXPIRED' ? 'منقضی شده' :
                                    offer?.status === 'ACCEPTED' ? 'تایید شده' :
                                        offer?.status === 'PENDING' ? 'بی‌پاسخ' :
                                            offer?.status === 'REJECTED' ? 'رد شده' :
                                                ''
                            }}
                        </span>
                    </div>

                    <!-- discount  -->
                    <div v-if="offer?.discount?.currentUser"
                        class=" w-fit flex items-center justify-center text-[13px] bg-[#f3d0f2] text-[#8c228a] font-medium h-10 rounded-[10px] px-5">
                        دارای تخفیف
                    </div>
                </div>
                <!-- edit & delete  -->
                <div class="w-full lg:w-auto flex flex-row items-center gap-2">
                    <Button v-if="offer?.status === 'PENDING'" @click="showAcceptModal = true" :isAdmin="true"
                        class=" w-full lg:w-[81px] h-10! py-0 text-[13px] font-normal">
                        <span class="flex items-center justify-center gap-1.5 text-nowrap">
                            تایید
                        </span>
                    </Button>
                    <Button v-if="offer?.status === 'PENDING'" @click="showRejectModal = true" :isAdmin="true"
                        variant="outline"
                        class=" w-full lg:w-[81px]  border-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
                        <span class="text-nowrap flex items-center justify-center gap-2 ">
                            <!-- <img width="20" src="/svg/edit-icon.svg" /> -->
                            رد
                        </span>
                    </Button>
                    <Button v-if="offer?.status === 'PENDING'" @click="showDeleteModal = true" :isAdmin="true"
                        variant="outline"
                        class=" w-full lg:w-[81px]  border-2  rounded-[10px] h-10! py-0 text-[13px] font-normal">
                        <span class="flex items-center justify-center gap-2 ">
                            <!-- <img width="19" src="/svg/trash-icon2.svg" /> -->
                            حذف
                        </span>
                    </Button>
                </div>
            </div>

        </div>

        <div class="w-full flex flex-col  p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
            <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.773px]">
                <div class="w-full lg:w-1/2 flex flex-col gap-3">
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">تاریخ ثبت:</span>
                        <span dir="ltr" class="font-fa text-[#1b325a]">{{ stringToFaDateTime(offer?.createdAt) }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">پیشنهاد دهنده:</span>
                        <span class="text-[#1b325a]">
                            <RouterLink v-if="offer?.owner?.id" :to="`/users/${offer.owner.uuid}`"
                                class="text-[#2d5396] hover:underline">
                                {{ offer?.owner?.fullName }}
                            </RouterLink>
                            <span v-else>{{ offer?.owner?.fullName }}</span>
                            <span class="font-fa">( {{ offer?.owner?.id }})</span>
                        </span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">نوع پیشنهاد:</span>
                        <span class=" text-[#1b325a]">{{ cardType ? 'خرید' : 'فروش' }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">کشور:</span>

                        <div v-if="offer?.requisition?.type === 'SELL'"
                            class="flex items-center gap-1 text-sm font-medium text-[##1b325a]">
                            <img :src="offer?.bankAccountCountries?.internationalBuyer?.flagUrl ||
                                offer?.bankAccountCountries?.internationalPaypalBuyer?.flagUrl ||
                                offer?.bankAccountCountries?.internationalRevolutBuyer?.flagUrl" width="22" height="16"
                                alt="flag" />
                            {{ offer?.bankAccountCountries?.internationalBuyer?.title?.fa ||
                                offer?.bankAccountCountries?.internationalPaypalBuyer?.title?.fa ||
                                offer?.bankAccountCountries?.internationalRevolutBuyer?.title?.fa }}
                        </div>


                        <div v-if="offer?.requisition?.type === 'BUY'"
                            class="flex items-center gap-1 text-sm font-medium text-[##1b325a]">
                            <img :src="offer?.bankAccountCountries?.seller?.flagUrl" width="22" height="16"
                                alt="flag" />
                            {{ offer?.bankAccountCountries?.seller?.title?.fa }}
                        </div>


                    </div>
                </div>
                <div class="w-full lg:w-1/2 flex flex-col gap-3">
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">ارز:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.currency?.titleFa }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">مقدار:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.currencyAmount.toLocaleString() }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">نرخ تبدیل:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.exchangeRate?.toLocaleString() }} تومان</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">مبلغ کل:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.sellerReceivableRialAmount?.toLocaleString() }}
                            تومان</span>
                    </div>
                    <div v-if="offer?.discount?.currentUser?.rate"
                        class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">تخفیف:</span>
                        <span class="font-fa text-[#1b325a]">
                            {{ offer.discount?.currentUser?.rate }}%
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-4 mt-8 text-[#1b325a] text-[22px]">
            درخواست پایه
        </div>

        <div class="w-full flex flex-col  p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
            <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.773px]">
                <div class="w-full lg:w-1/2 flex flex-col gap-3">
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">تاریخ ثبت:</span>
                        <span dir="ltr" class="font-fa text-[#1b325a]">{{
                            stringToFaDateTime(offer?.requisition?.createdAt) }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">درخواست کننده:</span>
                        <span class="text-[#1b325a]">
                            <RouterLink v-if="offer?.requisition?.owner?.id" :to="`/users/${offer.requisition.owner.uuid}`"
                                class="text-[#2d5396] hover:underline">
                                {{ offer?.requisition?.owner?.fullName }}
                            </RouterLink>
                            <span v-else>{{ offer?.requisition?.owner?.fullName }}</span>
                        </span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">نوع درخواست:</span>
                        <span class=" text-[#1b325a]">{{ offer?.requisition?.type === "SELL" ? 'فروش' : 'خرید' }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">کشور:</span>
                        <div v-if="offer?.requisition?.type === 'SELL' && offer?.requisition?.country.id"
                            class="flex items-center">
                            <img :src="offer?.requisition?.country.flagUrl" class="me-1" width="22" max-width="22" />
                            <span>{{ offer?.requisition?.country.title.fa }}</span>
                        </div>
                        <div v-else-if="offer?.requisition?.type === 'BUY' && offer?.requisition?.countries">
                            <div v-for="(country, index) in offer?.requisition?.countries" :key="index"
                                class="flex items-center">
                                <img :src="country.flagUrl" class="me-1" width="22" max-width="22" />

                                <span v-if="index === 0">{{ country.title.fa }}</span>
                                <span v-else>{{ country.title.fa }}</span>

                                <span v-if="(index as number) < offer?.requisition?.countries.length - 1">,</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="w-full lg:w-1/2 flex flex-col gap-3">
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">ارز:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.requisition?.currency?.titleFa }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">مقدار:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.requisition?.currencyAmount.toLocaleString()
                            }}</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">نرخ تبدیل:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.requisition?.exchangeRate?.toLocaleString() }}
                            تومان</span>
                    </div>
                    <div class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">مبلغ کل:</span>
                        <span class="font-fa text-[#1b325a]">{{ offer?.requisition?.finalRialAmount?.toLocaleString() }}
                            تومان</span>
                    </div>
                    <div v-if="offer?.requisition?.discount?.currentUser"
                        class="w-full flex items-center justify-between  text-sm font-medium">
                        <span class="text-[#2d5396]">تخفیف:</span>
                        <span class="font-fa text-[#1b325a]">
                            {{ offer?.requisition?.discount?.currentUser?.rate }}%
                        </span>
                    </div>
                </div>
            </div>
            <!-- Description -->
            <div v-if="offer?.requisition?.description"
                class="border-t border-[#eaeef5] pt-5 text-[13px] text-[#4e648c] font-medium mt-5">
                توضیحات: {{ offer?.requisition?.description }}
            </div>
        </div>
    </div>
</template>
